import React, { useState, useEffect, memo } from "react";
import { Tabs, Tab, ListGroup, Button } from "react-bootstrap";
import "./styles.scss";
import { DownloadButtons } from "./constants";
import { downloadPdf } from "./hooks";
import {
  getAttendedLiveClasses,
  getMissedClasses,
} from "../../../utils/liveClasses";
import Loader from "../../../components/Loader";
import EmptyScreen from "../../../components/EmptyScreen";
import { useNavigate } from "react-router-dom";
import withSubscriptionCheckButton from "../../../components/Common/withSubscriptionCheckButton";

const ClassesAttended = ({
  classroomId,
  isMobileView,
  // hasValidSubscription,
  keyType = "",
  loading = false,
  studentId,
}) => {
  const [key, setKey] = useState(keyType !== "" ? keyType : "attended");
  const [isLoadingAttended, setIsLoadingAttended] = useState(false);
  const [isLoadingMissed, setIsLoadingMissed] = useState(false);
  const [attendedClasses, setAttendedClasses] = useState([]);
  const [missedClasses, setMissedClasses] = useState([]);
  const PlayRecordingButton = studentId
    ? Button
    : withSubscriptionCheckButton(Button, "Live Classes");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAttendedClasses = async () => {
      setIsLoadingAttended(true);
      try {
        if (classroomId) {
          const attendedResponse = await getAttendedLiveClasses(
            classroomId,
            studentId,
          );
          if (attendedResponse?.count) {
            setAttendedClasses(attendedResponse.liveClasses);
          } else {
            console.error(
              "Error fetching attended classes:",
              attendedResponse.error,
            );
          }
        }
      } catch (error) {
        console.error("Error fetching attended classes:", error.message);
      } finally {
        setIsLoadingAttended(false);
      }
    };

    const fetchMissedClasses = async () => {
      setIsLoadingMissed(true);
      try {
        if (classroomId) {
          const missedResponse = await getMissedClasses(classroomId, studentId);

          if (missedResponse?.count) {
            setMissedClasses(missedResponse.liveClasses);
          } else {
            console.error(
              "Error fetching missed classes:",
              missedResponse.error,
            );
          }
        }
      } catch (error) {
        console.error("Error fetching missed classes:", error.message);
      } finally {
        setIsLoadingMissed(false);
      }
    };
    if (classroomId) {
      fetchAttendedClasses();
      fetchMissedClasses();
    }
  }, [classroomId, studentId]);

  DownloadButtons[0].onClick = () => {
    downloadPdf("Classes-Attended");
  };

  const playRecordings = async (liveclassid) => {
    navigate(
      `/student/myClassrooms/${classroomId}/recordings?liveclass=${liveclassid}`,
    );
  };

  const truncateName = (name, maxLength = 20) => {
    return name.length > maxLength
      ? `${name.substring(0, maxLength)}...`
      : name;
  };

  if (isLoadingAttended || isLoadingMissed) {
    return <Loader />;
  }

  return (
    <div
      style={{ margin: "10px", paddingBottom: isMobileView ? "20px" : "" }}
      id="Classes-Attended"
    >
      {/* <CreationHeader
        style={{ padding: "0px 20px" }}
        heading={"Classes Attended"}
      /> */}
      <div className="row px-4 py-2">
        <h5>
          Classes you {keyType === "attended" ? "attended" : "missed"} last week
        </h5>
        <Tabs
          id="class-recordings-tabs"
          activeKey={key}
          onSelect={(k) => setKey(k)}
          className="mb-3 mt-3"
        >
          <Tab eventKey="attended" title="Attended">
            <ListGroup variant="flush">
              {attendedClasses.length > 0 ? (
                attendedClasses?.map((classItem, index) => (
                  <ListGroup.Item
                    key={index}
                    className="d-flex justify-content-between align-items-center gap-3"
                    style={{ background: "#fcfcfc" }}
                  >
                    <div>
                      <div>
                        <strong>
                          <strong>{truncateName(classItem.name, 35)}</strong>
                        </strong>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <span className="class-details">Joined at:</span>{" "}
                          <span className="class-details-light">
                            {new Date(
                              classItem.startDateTime,
                            ).toLocaleDateString()}{" "}
                            -{" "}
                            {new Date(
                              classItem.startDateTime,
                            ).toLocaleTimeString()}
                          </span>{" "}
                        </div>
                        <div>
                          <span className="class-details">Duration:</span>{" "}
                          <span className="class-details-light">
                            {classItem.duration} minutes
                          </span>{" "}
                        </div>
                      </div>
                    </div>
                    {classItem?.hasRecordingFile && !loading && (
                      <span>
                        <PlayRecordingButton
                          onClick={() => {
                            playRecordings(classItem._id);
                          }}
                          classRoomId={classroomId}
                          subjects={classItem.subjects} // Pass the subjects array
                        >
                          Play Recording
                        </PlayRecordingButton>
                      </span>
                    )}
                  </ListGroup.Item>
                ))
              ) : (
                <EmptyScreen
                  heading="No Classes Attended"
                  subHeading="Not enough data to show attended classes"
                />
              )}
            </ListGroup>
          </Tab>
          <Tab eventKey="missed" title="Missed">
            <ListGroup variant="flush">
              {missedClasses.length > 0 ? (
                missedClasses?.map((classItem, index) => (
                  <ListGroup.Item
                    key={index}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div>
                        <strong>{truncateName(classItem.name, 35)}</strong>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <span className="class-details">Scheduled at:</span>{" "}
                          <span className="class-details-light">
                            {new Date(
                              classItem.startDateTime,
                            ).toLocaleDateString()}{" "}
                            -{" "}
                            {new Date(
                              classItem.startDateTime,
                            ).toLocaleTimeString()}
                          </span>{" "}
                        </div>
                        <div>
                          <span className="class-details">Duration:</span>{" "}
                          <span className="class-details-light">
                            {classItem.duration} minutes
                          </span>{" "}
                        </div>
                      </div>
                    </div>
                    {!loading && (
                      <span>
                        <PlayRecordingButton
                          onClick={() => {
                            playRecordings(classItem._id);
                          }}
                          classRoomId={classroomId}
                          subjects={classItem.subjects} // Pass the subjects array
                        >
                          Play Recording
                        </PlayRecordingButton>
                      </span>
                    )}
                  </ListGroup.Item>
                ))
              ) : (
                <EmptyScreen
                  heading="No Classes Missed"
                  subHeading="Not enough data to show missed classes"
                />
              )}
            </ListGroup>
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default memo(ClassesAttended);
